import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
import { RequireAuth, useIsAuthenticated } from 'react-auth-kit';
// layouts
import DashboardLayout from '../layouts/dashboards';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';

import LoadingScreen from '../components/LoadingScreen';

const Loadable = (Component) => (props) => {
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const { pathname } = useLocation();

	return (
		<Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
			<Component {...props} />
		</Suspense>
	);
};
// eslint-disable-next-line react/prop-types
const PrivateRoute = ({ children, loginPath }) => {
	const isAuthenticated = useIsAuthenticated();
	const location = useLocation();

	if (isAuthenticated()) {
		return children;
	}

	return <Navigate to={loginPath} state={{ from: location }} replace />;
};

// eslint-disable-next-line react/prop-types
const GuestRoute = ({ children }) => {
	const isAuthenticated = useIsAuthenticated();

	if (isAuthenticated()) {
		return <Navigate to="/dashboard/app" replace />;
	}

	return <>{children}</>;
};

export default function Router() {
	return useRoutes([
		{
			path: 'auth',
			children: [
				{
					path: 'login',
					element: (
						<GuestRoute>
							<Login />
						</GuestRoute>
					),
				},
				{
					path: 'verify-email',
					element: <VerifyEmail />,
				},
				{
					path: 'reset-password',
					element: <ResetPassword />,
				},

				{
					path: 'forgot-password',
					element: <ForgotPassword />,
				},

				{
					path: 'admin',
					children: [{ path: 'reset-password', element: <ResetPasswordAdmin /> }],
				},
			],
		},
		{
			path: 'dashboard',
			element: (
				<RequireAuth loginPath={'/auth/login'}>
					<DashboardLayout />
				</RequireAuth>
			),
			children: [
				{ element: <Navigate to="/dashboard/app" replace />, index: true },
				{ path: 'app', element: <DashboardApp /> },
				{ path: 'forum', element: <Forum /> },
				{ path: 'chats', element: <Chat /> },
				{ path: 'announcement', element: <Announcement /> },
				{ path: 'events', element: <CreateEvent /> },
				{ path: 'profiling', element: <ProfilingApp /> },

				{
					path: 'member',
					children: [
						{ element: <Navigate to="/dashboard/member/list" replace />, index: true },
						{ path: 'list', element: <MemberList /> },
						{ path: 'unverified/list', element: <UnverifiedList /> },
						{ path: 'unverified/details/:memberID', element: <UnverifiedDetail /> },
						{ path: 'details/:memberID', element: <MemberDetail /> },
						{ path: 'details/edit/:memberID', element: <EditProfileApp /> },
					],
				},

				{
					path: 'analytics',
					children: [
						{
							element: <Navigate to="/dashboard/analytics/disability" replace />,
							index: true,
						},
						{ path: 'disability', element: <AnalyticsDisability /> },
						{ path: 'district', element: <AnalyticsDistrict /> },
						{ path: 'assistance', element: <AnalyticsAssistance /> },
						{ path: 'summary', element: <AnalyticsSummary /> },
					],
				},

				// { path: 'mapping', element: <Mapping /> },

				{
					path: 'request',
					children: [
						{
							element: <Navigate to="/dashboard/request/approved" replace />,
							index: true,
						},
						{ path: 'approved', element: <ApprovedList /> },
						{ path: 'pending', element: <PendingList /> },
						{ path: 'disapproved', element: <DisapprovedList /> },
						{ path: 'add_request', element: <AddRequest /> },
					],
				},
				{
					path: 'assistive',
					children: [
						{
							element: <Navigate to="/dashboard/assistive/create" replace />,
							index: true,
						},
						{ path: 'create', element: <AddNewAssistive /> },
						{ path: 'logs', element: <AssistiveLogs /> },
						{ path: 'list', element: <AssistiveStock /> },
						{ path: 'edit/:assistiveId', element: <EditAssistiveDevice /> },
						// { path: 'requestDetails', element: <RequestDetails /> },
					],
				},
			],
		},

		{
			path: '/',
			element: <LogoOnlyLayout />,
			children: [
				{ path: '/', element: <Navigate to="/dashboard/app" /> },
				{ path: '404', element: <NotFound /> },
				{ path: '*', element: <Navigate to="/404" /> },
			],
		},
		{
			path: '*',
			element: <Navigate to="/404" replace />,
		},
	]);
}

// DASHBOARD
const DashboardApp = Loadable(lazy(() => import('../pages/dashboard/DashboardApp')));

// Member
const MemberList = Loadable(lazy(() => import('../pages/members/MemberList')));
const UnverifiedList = Loadable(lazy(() => import('../pages/members/UnverifiedList')));
const UnverifiedDetail = Loadable(lazy(() => import('../pages/members/UnverifiedDetail')));
const MemberDetail = Loadable(lazy(() => import('../pages/members/MemberDetail')));

// Profiling
const ProfilingApp = Loadable(lazy(() => import('../pages/profiling/ProfilingApp')));
const EditProfileApp = Loadable(lazy(() => import('../pages/profiling/EditProfileApp')));

// Events
const CreateEvent = Loadable(lazy(() => import('../pages/event/CreateEvent')));

// Assistive
const AddNewAssistive = Loadable(lazy(() => import('../pages/assistive/AddNewAssistive')));
const AssistiveLogs = Loadable(lazy(() => import('../pages/assistive/AssistiveLogs')));
const EditAssistiveDevice = Loadable(lazy(() => import('../pages/assistive/EditAssistiveDevice')));
// const RequestDetails = Loadable(lazy(() => import('../pages/assistive/RequestDetails')));
const AssistiveStock = Loadable(lazy(() => import('../pages/assistive/AssistiveStock')));

// Analytics
const AnalyticsDisability = Loadable(lazy(() => import('../pages/analytics/AnalyticsDisability')));
const AnalyticsDistrict = Loadable(lazy(() => import('../pages/analytics/AnalyticsDistrict')));
const AnalyticsAssistance = Loadable(lazy(() => import('../pages/analytics/AnalyticsAssistance')));
const AnalyticsSummary = Loadable(lazy(() => import('../pages/analytics/AnalyticsSummary')));

// Requests
const ApprovedList = Loadable(lazy(() => import('../pages/requests/ApprovedList')));
const PendingList = Loadable(lazy(() => import('../pages/requests/PendingList')));
const DisapprovedList = Loadable(lazy(() => import('../pages/requests/DisapprovedList')));
const AddRequest = Loadable(lazy(() => import('../pages/requests/AddRequest')));

// Mapping
const Mapping = Loadable(lazy(() => import('../pages/map/Mapping')));

// Announcment
const Announcement = Loadable(lazy(() => import('../pages/announcement/Announcement')));

// Announcment
const Chat = Loadable(lazy(() => import('../pages/chats/Chat')));

// Forums
const Forum = Loadable(lazy(() => import('../pages/forum/Forum')));

// Login
const Login = Loadable(lazy(() => import('../pages/auth/Login')));

const VerifyEmail = Loadable(lazy(() => import('../pages/auth/VerifyEmail')));

const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));

const ResetPasswordAdmin = Loadable(lazy(() => import('../pages/auth/admin/ResetPasswordAdmin')));

const ForgotPassword = Loadable(lazy(() => import('../pages/auth/ForgotPassword')));

// Not found
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
